/**
 * frontend/src/lib/mots.ts
 * `#837` — **UNE TABLE QUI PORTE DES CLÉS *ET* DES INVARIANTS.**
 * `#839` — **PROMU DU LABO AU SOCLE.** *Il est né dans le Labo, où six entités
 *   mêlaient quatre clés et deux invariants dans le même `Record`. Le chrome a
 *   exactement le même besoin — `Cluster` et `Module` sont des invariants à
 *   côté de `PDV` et `Marque`. **Un type qui sert deux familles ne vit pas dans
 *   l'une des deux** (`#819`, appliqué au code plutôt qu'aux chaînes).*
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠⚠ **POURQUOI CE PETIT TYPE EXISTE.**
 * ══════════════════════════════════════════════════════════════════════════════
 *   *Le Labo range presque tous ses libellés dans des **tables de mots hors
 *   JSX** — 30 d'après le T0 `#835` §2 ⑵. Le patron `#810` dit de porter une
 *   `CleI18n` dans la table et de la résoudre au rendu. **Mais le `#800` dit
 *   qu'un invariant reste LITTÉRAL, hors catalogue** — et les tables du Labo
 *   mêlent les deux : les six entités de classement sont `PDV`, `Wilaya`,
 *   `Région`, **`SKU`**, `Marque`, **`Merch`** — quatre clés et deux invariants,
 *   dans le même `Record`.*
 *
 *   *Sans ce type, il fallait choisir : trahir le `#800` en cataloguant `SKU`,
 *   ou trahir le `#810` en laissant la table en `string` et en perdant le
 *   typage des clés. **Le `Mot` permet les deux règles à la fois**, et il rend
 *   la décision LISIBLE dans la table : `cle('commun.pdv')` à côté de
 *   `litteral('SKU')`, on voit d'un coup d'œil ce qui se traduit.*
 *
 * ⚠ *`dire()` prend `t` en PARAMÈTRE — c'est le patron `RowTr` du `#810` : une
 *   fonction pure ne va pas chercher le traducteur, on le lui passe.*
 */
import type { CleI18n, CleT } from '@i18n';

/** Le traducteur, passé en paramètre aux fonctions pures (patron `#810`). */
export type TraduireI18n = (cle: CleT, params?: Record<string, string | number>) => string;

/** Un mot d'écran : soit une clé de catalogue, soit un invariant `#800`. */
export type Mot = { readonly cle: CleI18n } | { readonly litteral: string };

export const cle = (c: CleI18n): Mot => ({ cle: c });

/**
 * ⚠ *Un `litteral` n'est PAS un oubli de traduction : c'est une **décision
 *   `#800`**, prise par Hkm et listée dans le tableau de validation. Toute
 *   nouvelle entrée ici doit passer par lui (`#821` : un invariant se DÉCIDE).*
 */
export const litteral = (m: string): Mot => ({ litteral: m });

export function dire(m: Mot, t: TraduireI18n): string {
  return 'cle' in m ? t(m.cle) : m.litteral;
}
